import { useState, useEffect, useRef, type MouseEvent, type ElementType } from 'react';
import { useNotificationPermission } from '@/hooks/use-notification-permission';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import type { UserWithCommunities, Community, NavigationItemId } from '@/types';
import { communityEventsNavLabel, navigationItemPath } from '@/lib/community-navigation';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import {
  Building2,
  User,
  Target,
  Home,
  UsersRound,
  Calendar,
  PlusSquare,
  Settings,
  CreditCard,
  ChevronDown,
  LogOut,
  Bell,
  Menu,
  MessageSquareText,
  ClipboardList,
  Megaphone,
  X,
  Flag,
  Award,
  Moon,
  Sun,
  Monitor,
  Handshake,
} from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { canCreateCommunity, canJoinMoreCommunities } from '@/lib/community-permissions';
import { motion } from 'framer-motion';
import { useTheme } from '@/components/theme-context';
import { useTenant } from '@/providers/tenant-context';
import {
  getCommunityUiMode,
  getShellAdminNavItems,
  getShellMainNavIds,
} from '@/lib/community-ui-mode';
import { CommunitySwitcherMenu } from '@/components/dashboard/community-switcher-menu';
import { notifyNativeSafeAreaTheme } from '@/lib/native-safe-area-theme';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

interface DashboardShellProps {
  user: UserWithCommunities | null | undefined;
  currentCommunity: Community;
  allCommunities: Community[];
  children: React.ReactNode;
  // If false, hide member navigation (sidebar / mobile nav) and footer-like mobile nav.
  showMemberNavigation?: boolean;
  /** PENDING membership: nav is Home (→ `…/dashboard`, shows pending gate) and Profile only — no Directory/Create. */
  membershipPendingRestricted?: boolean;
}

// Define all available navigation items with their metadata outside component to keep it stable
const allNavItems = {
  home: { id: 'home', label: 'Dashboard', icon: Home },
  members: { id: 'members', label: 'Members', icon: UsersRound },
  events: { id: 'events', label: 'Events', icon: Calendar },
  projects: { id: 'projects', label: 'Projects', icon: Target },
  create: { id: 'create', label: 'Create', icon: PlusSquare },
  business: { id: 'business', label: 'Directory', icon: Building2 },
  'gives-asks': { id: 'gives-asks', label: 'Gives & Asks', icon: Handshake },
  profile: { id: 'profile', label: 'Profile', icon: User },
  membership: { id: 'membership', label: 'Membership', icon: CreditCard },
  settings: { id: 'settings', label: 'Settings', icon: Settings },
};

interface NavLinkProps {
  item: { id: string; label: string; icon: ElementType };
  mobile?: boolean;
  active: boolean;
  getLink: (id: string) => string;
  badgeCount?: number;
  mobilePosition?: 'first' | 'middle' | 'last';
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

const NavLink = ({
  item,
  mobile = false,
  active,
  getLink,
  badgeCount,
  mobilePosition: _mobilePosition = 'middle',
  onClick,
}: NavLinkProps) => (
  <Link
    to={getLink(item.id)}
    onClick={onClick}
    className={cn(
      mobile
        ? 'flex flex-col items-center justify-center w-16 h-16 rounded-[5px] transition-all duration-300 relative'
        : 'flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 group relative',
      active
        ? mobile
          ? 'text-primary bg-white dark:bg-zinc-800 rounded-[5px] shadow-md'
          : 'text-primary bg-primary/10 font-bold shadow-sm'
        : mobile
          ? 'text-primary-foreground'
          : 'text-muted-foreground',
      !mobile && !active && 'hover:text-foreground hover:bg-accent/50'
    )}
  >
    <item.icon
      className={cn(
        mobile ? 'h-6 w-6' : 'h-5 w-5 transition-transform group-hover:scale-110',
        active
          ? 'text-primary'
          : mobile
            ? 'text-primary-foreground'
            : 'text-muted-foreground group-hover:text-foreground'
      )}
    />
    <span
      className={cn(
        mobile ? 'text-[10px] font-bold mt-1 uppercase tracking-tighter' : 'font-medium'
      )}
    >
      {item.label}
    </span>
    {badgeCount !== undefined && badgeCount > 0 && (
      <span
        className={cn(
          'flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-bold text-destructive-foreground',
          mobile ? 'absolute top-1 right-2' : 'ml-auto relative z-10'
        )}
      >
        {badgeCount}
      </span>
    )}
    {active && (
      <motion.div
        layoutId={mobile ? 'mobileNav' : 'activeNav'}
        className={cn(
          'absolute rounded-full',
          mobile ? 'hidden' : 'left-0 w-1 h-6 rounded-r-full bg-primary'
        )}
      />
    )}
  </Link>
);

const DASHBOARD_VIEWS = ['social', 'business', 'projects'] as const;
type DashboardView = (typeof DASHBOARD_VIEWS)[number];

function availableDashboardViews(community: {
  isBusinessEnabled?: boolean;
  isProjectDashboardEnabled?: boolean;
}): DashboardView[] {
  const views: DashboardView[] = ['social'];
  if (community.isBusinessEnabled) views.push('business');
  if (community.isProjectDashboardEnabled) views.push('projects');
  return views;
}

function defaultDashboardView(community: {
  isBusinessEnabled?: boolean;
  isProjectDashboardEnabled?: boolean;
}): DashboardView {
  if (community.isBusinessEnabled) return 'business';
  if (community.isProjectDashboardEnabled) return 'projects';
  return 'social';
}

function resolveDashboardView(
  search: string,
  community: {
    isBusinessEnabled?: boolean;
    isProjectDashboardEnabled?: boolean;
  }
): DashboardView {
  const views = availableDashboardViews(community);
  const param = new URLSearchParams(search).get('view');
  if (
    param &&
    (DASHBOARD_VIEWS as readonly string[]).includes(param) &&
    views.includes(param as DashboardView)
  ) {
    return param as DashboardView;
  }
  const stored = localStorage.getItem('dashboardView');
  if (stored && views.includes(stored as DashboardView)) {
    return stored as DashboardView;
  }
  return defaultDashboardView(community);
}

export function DashboardShell({
  user,
  currentCommunity,
  allCommunities,
  children,
  showMemberNavigation = true,
  membershipPendingRestricted = false,
}: DashboardShellProps) {
  const uiMode = getCommunityUiMode(currentCommunity);
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { tenant, isWhitelabel } = useTenant();
  const { theme, setTheme } = useTheme();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(false);
  const [isCommunitySwitcherOpen, setIsCommunitySwitcherOpen] = useState(false);
  const navScrollRef = useRef<HTMLDivElement>(null);
  const mainScrollRef = useRef<HTMLElement>(null);

  useEffect(() => {
    notifyNativeSafeAreaTheme('primary');
    return () => notifyNativeSafeAreaTheme('background');
  }, []);

  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const update = () => {
      setShowLeftShadow(el.scrollLeft > 0);
      setShowRightShadow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [showMemberNavigation]);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isSwiping = useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isSwiping.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!sidebarRef.current) return;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX.current;
    const diffY = currentY - touchStartY.current;

    // Check if horizontal swipe to left is intended
    if (!isSwiping.current) {
      if (Math.abs(diffX) > Math.abs(diffY) && diffX < 0) {
        isSwiping.current = true;
      }
    }

    if (isSwiping.current) {
      // Prevent default scrolling behavior
      if (e.cancelable) {
        e.preventDefault();
      }
      const translateX = Math.min(0, diffX);
      sidebarRef.current.style.transform = `translateX(${translateX}px)`;
      sidebarRef.current.style.transition = 'none';
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!sidebarRef.current || !isSwiping.current) return;
    const currentX = e.changedTouches[0].clientX;
    const diffX = currentX - touchStartX.current;

    sidebarRef.current.style.transition = 'transform 0.25s cubic-bezier(0.16, 1, 0.3, 1)';

    if (diffX < -80) {
      // Closed by swiping: slide it completely off-screen and trigger state change
      sidebarRef.current.style.transform = 'translateX(-100%)';
      setTimeout(() => {
        setIsMobileMenuOpen(false);
      }, 150);
    } else {
      // Cancelled swipe: reset position to open
      sidebarRef.current.style.transform = 'translateX(0)';
    }
    isSwiping.current = false;
  };

  useEffect(() => {
    if (isMobileMenuOpen && sidebarRef.current) {
      sidebarRef.current.style.transform = '';
      sidebarRef.current.style.transition = '';
    }
  }, [isMobileMenuOpen]);

  const { hasPushNotificationsEnabled, requestPushNotifications } = useNotificationPermission();

  const { data: notificationsData } = useSWR('/api/notifications', fetcher, {
    refreshInterval: 15000,
  });
  const unreadCount = notificationsData?.unreadCount ?? 0;

  const handleSignOut = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const getLink = (item: string) => {
    if (item === 'sub-communities') return `/${currentCommunity.id}/sub-communities`;
    if (
      [
        'home',
        'members',
        'events',
        'projects',
        'create',
        'business',
        'gives-asks',
        'profile',
      ].includes(item)
    ) {
      return navigationItemPath(currentCommunity.id, item as NavigationItemId);
    }
    return `/${currentCommunity.id}/${item}`;
  };

  const isActive = (item: string) => {
    if (item === 'home') return pathname === getLink('home');

    // Exception: Creating or editing a business from profile should highlight Profile tab, not Create
    if (pathname.includes('/create/business')) {
      return item === 'profile';
    }

    if (item === 'events') {
      return (
        pathname === `/${currentCommunity.id}/events` ||
        pathname.startsWith(`/${currentCommunity.id}/events/`)
      );
    }

    const path = getLink(item);
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  const handleMobileHomeTabClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isActive('home')) return;

    event.preventDefault();
    const main = mainScrollRef.current;
    const nearTop = !main || main.scrollTop < 32;

    if (!nearTop) {
      main.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const views = availableDashboardViews(currentCommunity);
    if (views.length <= 1) {
      main?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const currentView = resolveDashboardView(location.search, currentCommunity);
    const nextView = views[(views.indexOf(currentView) + 1) % views.length];
    localStorage.setItem('dashboardView', nextView);
    const next = new URLSearchParams(location.search);
    next.set('view', nextView);
    navigate(`/${currentCommunity.id}/dashboard?${next.toString()}`, { replace: true });
    main?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const visibleNavIds = getShellMainNavIds(currentCommunity, user, uiMode);

  const mainNavItems = visibleNavIds.flatMap((itemId) => {
    const item = allNavItems[itemId];
    if (!item) return [];
    if (itemId === 'events') {
      return [{ ...item, label: communityEventsNavLabel(currentCommunity.id) }];
    }
    return [item];
  });

  const userCommunity = user?.communities.find((c) => c.id === currentCommunity.id);
  const userRoles = userCommunity?.roles || [];
  const userPermissions = userCommunity?.permissions || [];
  const isAdminOrCreator = userRoles.some((role) => ['ADMIN', 'CREATOR', 'OWNER'].includes(role));
  const isAdmin = userRoles.includes('ADMIN');
  const canManageNewMembers = isAdminOrCreator || userPermissions.includes('manage_new_members');
  const canCreate = canCreateCommunity({
    isWhitelabel,
    allowCreateCommunity: tenant?.allowCreateCommunity,
    communities: user?.communities ?? [],
  });
  const canJoin = canJoinMoreCommunities({
    isWhitelabel,
    catalogCount: tenant?.communityCount,
    joinedCount: user?.communities?.length ?? 0,
  });

  const { data: requestsData } = useSWR(
    canManageNewMembers && currentCommunity?.id && uiMode.pollMemberRequests
      ? `/api/communities/${currentCommunity.id}/requests`
      : null,
    fetcher,
    {
      refreshInterval: 30000,
    }
  );
  const pendingRequestsCount = requestsData?.requests?.length ?? 0;

  const adminNavConfig = getShellAdminNavItems(
    currentCommunity,
    isAdminOrCreator,
    uiMode,
    userPermissions
  );
  const adminNavIconById: Record<string, ElementType> = {
    settings: Settings,
    requests: UsersRound,
    'admin-members': UsersRound,
    'sub-communities': Building2,
    'informative-posts': MessageSquareText,
    'reported-posts': Flag,
    'business-report': ClipboardList,
    'points-dashboard': Award,
    advertisement: Megaphone,
    'membership-admin': CreditCard,
  };
  const adminNavItems = adminNavConfig.map((item) => ({
    ...item,
    icon: adminNavIconById[item.id] ?? Building2,
  }));
  const aggregatorNavItems = [...mainNavItems];
  if (uiMode.shellKind === 'aggregator' && isAdminOrCreator) {
    aggregatorNavItems.push(allNavItems.settings);
  }
  const shellMainNavItems = membershipPendingRestricted
    ? [{ ...allNavItems.home, label: 'Home' }, allNavItems.profile]
    : uiMode.shellKind === 'aggregator'
      ? aggregatorNavItems
      : mainNavItems;

  const shellAdminNavItems = membershipPendingRestricted
    ? []
    : uiMode.shellKind === 'aggregator' && isAdmin
      ? [
          { id: 'admin-members', label: 'Member Directory', icon: UsersRound },
          ...adminNavItems.filter((item) => item.id !== 'admin-members'),
        ]
      : adminNavItems;

  return (
    <div
      className="flex min-h-screen bg-background relative overflow-hidden transition-colors duration-300"
      style={{
        backgroundImage: [
          'radial-gradient(ellipse 65% 55% at 100% 0%, hsl(var(--primary) / 0.08), transparent)',
          'radial-gradient(ellipse 65% 55% at 0% 100%, hsl(var(--primary) / 0.06), transparent)',
          'linear-gradient(hsl(var(--primary) / 0.03), hsl(var(--primary) / 0.03))',
        ].join(', '),
      }}
    >
      {/* Sidebar - Desktop (lg breakpoint for tablet support) */}
      {showMemberNavigation && (
        <aside className="hidden lg:flex w-64 flex-col bg-background/95 backdrop-blur-xl border-r border-border/40 sticky top-0 h-screen z-40 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 scrollbar-premium">
            <Link className="flex items-center gap-2.5 group mb-8" to="/">
              {isWhitelabel && tenant?.logoWithName ? (
                <img
                  src={tenant.logoWithName}
                  alt={tenant.name}
                  className="h-10 w-auto object-contain group-hover:scale-105 transition-all"
                />
              ) : (
                <>
                  <img
                    src={isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'}
                    alt={isWhitelabel ? tenant?.name : 'Milora'}
                    className="w-10 h-10 object-contain group-hover:scale-105 transition-all"
                  />
                  <span className="font-bold text-2xl tracking-tighter text-foreground">
                    {isWhitelabel ? tenant?.name : 'Milora'}
                  </span>
                </>
              )}
            </Link>

            <div className="space-y-1">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] px-4 mb-4">
                Navigation
              </p>
              <nav className="space-y-2">
                {shellMainNavItems.map((item) => (
                  <NavLink key={item.id} item={item} active={isActive(item.id)} getLink={getLink} />
                ))}

                {shellAdminNavItems.length > 0 && (
                  <>
                    <div className="my-4 mx-4 border-t border-border/10" />
                    <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em] px-4 mb-2">
                      Admin
                    </p>
                    {shellAdminNavItems.map((item) => (
                      <NavLink
                        key={item.id}
                        item={item}
                        active={isActive(item.id)}
                        getLink={getLink}
                        badgeCount={item.id === 'requests' ? pendingRequestsCount : undefined}
                      />
                    ))}
                  </>
                )}
              </nav>
            </div>
          </div>

          <div className="p-6 border-t border-border/5 space-y-4 shrink-0">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full flex items-center justify-start gap-3 p-1.5 h-auto rounded-xl hover:bg-accent/50 border border-transparent hover:border-border/10 transition-all"
                  >
                    <Avatar className="h-9 w-9 border-2 border-primary/20">
                      <AvatarFallback className="bg-gradient-premium text-white text-xs">
                        {user?.firstName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start truncate text-left">
                      <span className="text-sm font-semibold text-foreground truncate w-full">
                        {user?.firstName} {user?.lastName}
                      </span>
                      <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
                        {isAdminOrCreator ? 'Administrator' : 'Member'}
                      </span>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 glass border-border/10 shadow-[0_20px_60px_-10px_hsl(var(--primary)/0.35),0_8px_24px_-8px_rgb(0_0_0/0.25)]"
                >
                  <DropdownMenuLabel className="text-foreground">My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-border/5" />
                  {uiMode.showCommunityProfileInMenu && (
                    <DropdownMenuItem
                      onClick={() => navigate(`/${currentCommunity.id}/profile`)}
                      className="hover:bg-accent/10 cursor-pointer text-foreground text-sm"
                    >
                      <User className="mr-2 h-4 w-4" /> Profile
                    </DropdownMenuItem>
                  )}
                  {uiMode.showCommunitySettingsInMenu && isAdminOrCreator && (
                    <DropdownMenuItem
                      onClick={() => navigate(`/${currentCommunity.id}/settings`)}
                      className="hover:bg-accent/10 cursor-pointer text-foreground text-sm"
                    >
                      <Settings className="mr-2 h-4 w-4" /> Community Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-border/5" />
                  <DropdownMenuItem
                    onClick={() => navigate('/account-settings')}
                    className="hover:bg-accent/10 cursor-pointer text-foreground text-sm"
                  >
                    <Settings className="mr-2 h-4 w-4" /> Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-border/5" />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-red-500 focus:text-red-400 hover:bg-red-500/10 cursor-pointer text-sm"
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                className="w-full gap-2 rounded-xl"
                onClick={() => {
                  const from = location.pathname + location.search + location.hash;
                  navigate(`/login?redirect=${encodeURIComponent(from)}`, { state: { from } });
                }}
              >
                <LogOut className="h-4 w-4 rotate-180" />
                Sign In
              </Button>
            )}
          </div>
        </aside>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Header */}
        <header className="h-20 flex items-stretch justify-between px-4 lg:px-8 bg-primary border-b border-primary-foreground/10 z-30 shadow-[0_8px_24px_-6px_rgba(0,0,0,0.28)]">
          <div className="flex items-stretch min-w-0 -ml-4 lg:-ml-8">
            {!isWhitelabel || (tenant?.communityCount && tenant.communityCount > 1) ? (
              <DropdownMenu
                open={isCommunitySwitcherOpen}
                onOpenChange={setIsCommunitySwitcherOpen}
              >
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex h-full items-center gap-2 lg:gap-3 rounded-none px-4 lg:px-6 hover:bg-white/10 group min-w-0"
                  >
                    <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center p-1 overflow-hidden group-hover:scale-105 transition-transform">
                      {currentCommunity?.logo ? (
                        <img
                          src={currentCommunity.logo}
                          alt={currentCommunity.name}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <Building2 className="h-4 w-4 text-primary-foreground" />
                      )}
                    </div>
                    <span className="font-bold text-primary-foreground tracking-tight truncate max-w-[120px] sm:max-w-[200px] lg:max-w-none">
                      {currentCommunity?.name}
                      {uiMode.headerBadge === 'aggregator' ? (
                        <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest opacity-70">
                          Aggregator
                        </span>
                      ) : null}
                    </span>
                    {allCommunities.some((c) => 'status' in c && c.status === 'INVITED') && (
                      <span className="ml-1 h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                    )}
                    <ChevronDown className="h-4 w-4 text-primary-foreground/60 group-hover:text-primary-foreground transition-colors shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-80 bg-background border-border/10 shadow-[0_20px_60px_-10px_hsl(var(--primary)/0.35),0_8px_24px_-8px_rgb(0_0_0/0.25)]"
                >
                  <DropdownMenuLabel className="text-muted-foreground text-xs uppercase tracking-widest p-4">
                    Communities
                  </DropdownMenuLabel>
                  <CommunitySwitcherMenu
                    communities={allCommunities}
                    currentCommunityId={currentCommunity.id}
                    user={user}
                    userId={user?.id}
                    onRequestClose={() => setIsCommunitySwitcherOpen(false)}
                  />
                  <div className="px-2 pb-2">
                    {(canCreate || canJoin) && (
                      <>
                        <DropdownMenuSeparator className="bg-border/5 my-2" />
                        <DropdownMenuItem asChild className="p-0">
                          <Link
                            to={`/create-community?parent=${currentCommunity.id}`}
                            className="flex items-center gap-3 w-full p-2 rounded-xl transition-colors cursor-pointer text-primary hover:bg-primary/5"
                            onClick={() => setIsCommunitySwitcherOpen(false)}
                          >
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                              <PlusSquare className="h-4 w-4" />
                            </div>
                            <span className="text-sm font-bold">
                              {canCreate ? 'Create or Join' : 'Join Community'}
                            </span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex h-full items-center gap-2 lg:gap-3 px-4 lg:px-6 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center p-1 overflow-hidden">
                  {currentCommunity?.logo ? (
                    <img
                      src={currentCommunity.logo}
                      alt={currentCommunity.name}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-4 w-4 text-primary-foreground" />
                  )}
                </div>
                <span className="font-bold text-primary-foreground tracking-tight truncate max-w-[120px] sm:max-w-[200px] lg:max-w-none">
                  {currentCommunity?.name}
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 lg:gap-4 shrink-0">
            <Link to="/notifications" className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="bg-white/10 border border-white/10 rounded-full h-10 w-10 text-primary-foreground hover:bg-white/20"
              >
                <Bell className="h-5 w-5" />
              </Button>
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 h-3 w-3 rounded-full bg-blue-600 border-2 border-background ring-1 ring-blue-300 animate-pulse" />
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/10 border border-white/10 rounded-full h-10 w-10 p-0 overflow-hidden hover:bg-white/20"
                    aria-label="User profile"
                  >
                    <Avatar className="h-full w-full">
                      {user.image ? (
                        <AvatarImage src={user.image} alt="" className="object-cover" />
                      ) : null}
                      <AvatarFallback className="bg-white/20 text-primary-foreground text-xs font-semibold">
                        {user.firstName?.[0]}
                        {user.lastName?.[0]}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-background border-border/10 shadow-[0_20px_60px_-10px_hsl(var(--primary)/0.35),0_8px_24px_-8px_rgb(0_0_0/0.25)]"
                >
                  <DropdownMenuLabel className="text-foreground">
                    <div className="flex flex-col gap-0.5">
                      <span>
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
                        {isAdminOrCreator ? 'Administrator' : 'Member'}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="h-[2px] bg-muted-foreground/35" />
                  {uiMode.showCommunityProfileInMenu && (
                    <DropdownMenuItem
                      onClick={() => navigate(`/${currentCommunity.id}/profile`)}
                      className="hover:bg-accent/10 cursor-pointer text-foreground text-sm"
                    >
                      <User className="mr-2 h-4 w-4" /> Profile
                    </DropdownMenuItem>
                  )}
                  {uiMode.showCommunitySettingsInMenu && isAdminOrCreator && (
                    <DropdownMenuItem
                      onClick={() => navigate(`/${currentCommunity.id}/settings`)}
                      className="hover:bg-accent/10 cursor-pointer text-foreground text-sm"
                    >
                      <Settings className="mr-2 h-4 w-4" /> Community Settings
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="h-[2px] bg-muted-foreground/35" />
                  <DropdownMenuItem
                    onClick={() => navigate('/account-settings')}
                    className="hover:bg-accent/10 cursor-pointer text-foreground text-sm"
                  >
                    <Settings className="mr-2 h-4 w-4" /> Account Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="h-[2px] bg-muted-foreground/35" />
                  <div className="px-2 py-2">
                    <p className="mb-2 px-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Theme
                    </p>
                    <div className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1">
                      {(
                        [
                          { value: 'light', label: 'Light', icon: Sun },
                          { value: 'dark', label: 'Dark', icon: Moon },
                          { value: 'system', label: 'Auto', icon: Monitor },
                        ] as const
                      ).map(({ value, label, icon: Icon }) => {
                        const isActive = theme === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setTheme(value)}
                            className={cn(
                              'flex items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-all',
                              isActive
                                ? 'bg-background text-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                            )}
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" />
                            <span>{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <DropdownMenuSeparator className="h-[2px] bg-muted-foreground/35" />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-red-500 focus:text-red-400 hover:bg-red-500/10 cursor-pointer text-sm"
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {showMemberNavigation && (
              <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/10 border border-white/10 rounded-full h-10 w-10"
                  >
                    <Menu className="h-5 w-5 text-primary-foreground" />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="left"
                  className="w-full p-0 border-none bg-transparent shadow-none h-[100dvh] max-h-[100dvh] overflow-hidden [&>button]:hidden"
                >
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <div
                    className="w-full h-full bg-transparent relative"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onClick={(e) => {
                      if (e.target === e.currentTarget) {
                        setIsMobileMenuOpen(false);
                      }
                    }}
                  >
                    <div
                      ref={sidebarRef}
                      className="flex flex-col h-full w-80 bg-background/95 backdrop-blur-xl border-r border-border/40 overflow-hidden shadow-xl"
                      style={{ willChange: 'transform' }}
                    >
                      {/* Fixed Mobile Header */}
                      <div className="p-6 border-b border-border/5 shrink-0 flex items-center justify-between">
                        <Link
                          className="flex items-center gap-2.5 group"
                          to="/"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {isWhitelabel && tenant?.logoWithName ? (
                            <img
                              src={tenant.logoWithName}
                              alt={tenant.name}
                              className="h-8 w-auto object-contain group-hover:scale-105 transition-all"
                            />
                          ) : (
                            <>
                              <img
                                src={
                                  isWhitelabel && tenant?.logo ? tenant.logo : '/milora-logo.png'
                                }
                                alt={isWhitelabel ? tenant?.name : 'Milora'}
                                className="w-8 h-8 object-contain group-hover:scale-105 transition-all"
                              />
                              <span className="font-bold text-xl tracking-tighter text-foreground">
                                {isWhitelabel ? tenant?.name : 'Milora'}
                              </span>
                            </>
                          )}
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent/50"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Scrollable Mobile Nav Area */}
                      <div className="flex-1 overflow-y-auto p-4 scrollbar-premium">
                        <nav className="space-y-1 mb-8">
                          {shellMainNavItems.map((item) => (
                            <Link
                              key={item.id}
                              to={getLink(item.id)}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={cn(
                                'flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all',
                                isActive(item.id)
                                  ? 'bg-primary/10 text-primary font-bold shadow-sm'
                                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                              )}
                            >
                              <item.icon className="h-5 w-5" />
                              <span className="text-base font-medium">{item.label}</span>
                            </Link>
                          ))}
                          {shellAdminNavItems.length > 0 && (
                            <div className="mt-8 border-t border-border/5 pt-6">
                              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] px-4 mb-3">
                                Admin
                              </p>
                              <div className="space-y-1">
                                {shellAdminNavItems.map((item) => (
                                  <Link
                                    key={item.id}
                                    to={`/${currentCommunity.id}/${item.id}`}
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className={cn(
                                      'flex items-center gap-3.5 px-4 py-3 rounded-xl transition-all w-full relative',
                                      isActive(item.id)
                                        ? 'bg-primary/10 text-primary font-bold shadow-sm'
                                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                                    )}
                                  >
                                    <item.icon className="h-5 w-5" />
                                    <span className="text-base font-medium">{item.label}</span>
                                    {item.id === 'requests' && pendingRequestsCount > 0 && (
                                      <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1.5 text-xs font-bold text-destructive-foreground">
                                        {pendingRequestsCount}
                                      </span>
                                    )}
                                  </Link>
                                ))}
                              </div>
                            </div>
                          )}
                        </nav>
                      </div>

                      {/* Fixed Mobile Footer */}
                      <div className="p-4 border-t border-border/5 shrink-0 bg-background/50 backdrop-blur-md">
                        {user ? (
                          <>
                            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-accent/5 border border-border/5 shadow-sm">
                              <Avatar className="h-10 w-10 border-2 border-primary/20">
                                <AvatarFallback className="bg-gradient-premium text-white text-sm">
                                  {user?.firstName?.[0]}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex flex-col overflow-hidden">
                                <span className="text-sm font-bold text-foreground truncate">
                                  {user?.firstName} {user?.lastName}
                                </span>
                                <span className="text-[9px] text-muted-foreground uppercase tracking-wider">
                                  {isAdminOrCreator ? 'Administrator' : 'Member'}
                                </span>
                              </div>
                            </div>
                            {!hasPushNotificationsEnabled && (
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  requestPushNotifications();
                                }}
                                className="w-full justify-start text-foreground gap-4 h-auto p-3 rounded-xl hover:bg-accent/5 transition-all group mb-2"
                              >
                                <Bell className="h-5 w-5 transition-transform group-hover:scale-110" />
                                <span className="text-base font-medium">Enable Notifications</span>
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                navigate('/account-settings');
                              }}
                              className="w-full justify-start text-foreground gap-4 h-auto p-3 rounded-xl hover:bg-accent/5 transition-all group mb-2"
                            >
                              <Settings className="h-5 w-5 transition-transform group-hover:scale-110" />
                              <span className="text-base font-medium">Account Settings</span>
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                setIsMobileMenuOpen(false);
                                handleSignOut();
                              }}
                              className="w-full justify-start text-red-500 gap-4 h-auto p-3 rounded-xl hover:bg-red-500/5 transition-all group"
                            >
                              <LogOut className="h-5 w-5 transition-transform group-hover:-translate-x-1" />
                              <span className="text-base font-medium">Log Out</span>
                            </Button>
                          </>
                        ) : (
                          <Button
                            className="w-full gap-4 h-auto p-4 rounded-xl shadow-lg"
                            onClick={() => {
                              setIsMobileMenuOpen(false);
                              const from = location.pathname + location.search + location.hash;
                              navigate(`/login?redirect=${encodeURIComponent(from)}`, {
                                state: { from },
                              });
                            }}
                          >
                            <LogOut className="h-5 w-5 rotate-180" />
                            <span className="text-base font-bold text-white">Sign In</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </div>
        </header>

        {/* Main View Transition Area */}
        <main
          ref={mainScrollRef}
          className="flex-1 overflow-y-auto p-4 lg:p-8 pb-32 lg:pb-8 relative scroll-smooth"
        >
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="container max-w-[1600px] mx-auto px-0 lg:px-8"
          >
            {children}
          </motion.div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      {showMemberNavigation && (
        <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-primary shadow-[0_-8px_30px_rgba(0,0,0,0.35)] overflow-hidden h-[72px]">
          <div
            ref={navScrollRef}
            className="flex items-center justify-around overflow-x-auto no-scrollbar h-full px-3 w-full"
          >
            <div className="flex items-center gap-1 min-w-full justify-around">
              {shellMainNavItems.map((item, i) => (
                <div key={item.id} className="flex-shrink-0">
                  <NavLink
                    item={item}
                    active={isActive(item.id)}
                    getLink={getLink}
                    mobile
                    mobilePosition={
                      i === 0 ? 'first' : i === shellMainNavItems.length - 1 ? 'last' : 'middle'
                    }
                    onClick={item.id === 'home' ? handleMobileHomeTabClick : undefined}
                  />
                </div>
              ))}
            </div>
          </div>
          {showLeftShadow && (
            <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-primary to-transparent" />
          )}
          {showRightShadow && (
            <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-primary to-transparent" />
          )}
        </div>
      )}
    </div>
  );
}
